import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { StatusBadge } from '../../components/StatusBadge';
import { useGetMeetingDetailsQuery } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const MeetingDetailsScreen = ({ route, navigation }: any) => {
  const { id } = route.params;
  const { data: meeting, isLoading } = useGetMeetingDetailsQuery(id);
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  if (isLoading || !meeting) {
    return (
      <View style={[globalStyles.container, { justifyContent: 'center', alignItems: 'center', backgroundColor: theme.bg }]}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Meeting Details" subtitle={`Request Ref: ${meeting.id.slice(0, 8)}`} />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}
      >
        {/* Status Card Header */}
        <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, alignItems: 'center', marginBottom: 20 }]}>
          <StatusBadge status={meeting.status} />
          <Text style={[styles.visitorTitle, { color: theme.textPrimary }]}>
            {meeting.visitorName}
          </Text>
          {meeting.visitorCompany && (
            <Text style={[styles.companyText, { color: theme.textSecondary }]}>
              {meeting.visitorCompany}
            </Text>
          )}

          <View style={[styles.gridRow, { borderTopColor: theme.border }]}>
            <View style={{ alignItems: 'center' }}>
              <Text style={styles.gridLabel}>Date</Text>
              <Text style={[styles.gridValue, { color: theme.textPrimary }]}>
                {meeting.meetingDate}
              </Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <Text style={styles.gridLabel}>Time Slot</Text>
              <Text style={[styles.gridValue, { color: COLORS.primary }]}>{meeting.meetingTime}</Text>
            </View>
          </View>
        </View>

        {/* Contact Info Card */}
        <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: 16 }]}>
          <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
            📞 Visitor Contact Information
          </Text>

          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <Text style={[styles.infoLabel, { color: theme.textSecondary }]}>Phone Number:</Text>
            <Text style={[styles.infoVal, { color: theme.textPrimary }]}>{meeting.visitorPhone}</Text>
          </View>

          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <Text style={[styles.infoLabel, { color: theme.textSecondary }]}>Email Address:</Text>
            <Text style={[styles.infoVal, { color: theme.textPrimary }]}>{meeting.visitorEmail}</Text>
          </View>

          <View style={{ marginTop: 10 }}>
            <Text style={[styles.infoLabel, { color: theme.textSecondary, marginBottom: 4 }]}>Meeting Purpose:</Text>
            <Text style={[styles.purposeBox, { color: theme.textPrimary }]}>
              {meeting.meetingPurpose}
            </Text>
          </View>
        </View>

        {/* Admin Remarks */}
        {meeting.adminRemarks && (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: 16 }]}>
            <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
              💬 Master Admin Remarks
            </Text>
            <Text style={styles.remarksText}>{meeting.adminRemarks}</Text>
          </View>
        )}

        {/* Notification Logs */}
        {meeting.notificationLogs && meeting.notificationLogs.length > 0 && (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: 20 }]}>
            <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
              📲 Automated Notifications Status
            </Text>
            {meeting.notificationLogs.map((log) => (
              <View key={log.id} style={[styles.logRow, { borderBottomColor: theme.border }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ fontSize: 16, marginRight: 8 }}>{log.type === 'EMAIL' ? '✉️' : '💬'}</Text>
                  <Text style={[styles.logText, { color: theme.textPrimary }]}>
                    {log.type} to {log.recipient}
                  </Text>
                </View>
                <View
                  style={[
                    styles.logBadge,
                    {
                      backgroundColor: log.status === 'SENT' ? COLORS.successBg : COLORS.dangerBg,
                      borderColor: log.status === 'SENT' ? COLORS.success : COLORS.danger,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.logBadgeText,
                      { color: log.status === 'SENT' ? COLORS.success : COLORS.danger },
                    ]}
                  >
                    {log.status}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}

        <TouchableOpacity onPress={() => navigation.goBack()} style={{ paddingVertical: 12, alignItems: 'center' }}>
          <Text style={styles.backBtnText}>← Back to List</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  visitorTitle: {
    fontSize: 22,
    fontWeight: '800',
    marginTop: 12,
  },
  companyText: {
    fontSize: 12,
    marginTop: 2,
  },
  gridRow: {
    flexDirection: 'row',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    width: '100%',
    justifyContent: 'space-around',
  },
  gridLabel: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    color: '#94A3B8',
  },
  gridValue: {
    fontSize: 14,
    fontWeight: '800',
    marginTop: 2,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '800',
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  infoLabel: {
    fontSize: 12,
  },
  infoVal: {
    fontSize: 12,
    fontWeight: '700',
  },
  purposeBox: {
    fontSize: 12,
    lineHeight: 18,
  },
  remarksText: {
    fontSize: 12,
    color: COLORS.warning,
    fontStyle: 'italic',
    fontWeight: '600',
  },
  logRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  logText: {
    fontSize: 12,
    fontWeight: '700',
  },
  logBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
  },
  logBadgeText: {
    fontSize: 10,
    fontWeight: '800',
  },
  backBtnText: {
    color: COLORS.primary,
    fontSize: 13,
    fontWeight: '800',
  },
});
